// Composizione del prompt finale e del riepilogo umano. Funzioni pure.

import {
  BUILDER_SCENES,
  BUILDER_TECHNIQUES,
  BUILDER_INTENSITIES,
  ATMOSPHERES,
  BUILDER_PALETTES,
  BUILDER_PAPERS,
  BUILDER_MATERIALS,
  FRAME_STYLES,
  FRAME_RICHNESS,
  FRAME_COVERAGES,
  FRAME_RELATIONS,
  GRADIENT_DIRECTIONS,
  GRADIENT_SPREADS,
  GRADIENT_INTENSITIES,
  SCENE_PLACEMENTS,
  SCENE_EXTENTS,
  CUSTOM_ELEMENT_KEY,
  SACRED_SCENE_KEY,
  SACRED_FRAMINGS,
  SACRED_PERSPECTIVES,
  SACRED_MOODS,
  findByKey,
  findElementItem,
  findSceneFade,
  findSacredFigure,
  findPaperGrain,
  findMaterialIntensity,
  findZoneCombo,
  elementFigureLevers,
  isGeometricFrameStyle,
} from './builderCatalog';
import {
  hasImagery,
  isPaperActive,
  supportsAtmosphere,
  resolvedGradientEndpoints,
} from './builderState';

const CENTER_CLAUSE =
  'Ampia area centrale completamente libera e uniforme, senza alcun testo né scritte, espressamente adatta a ospitare la foto e i dati del defunto con buona leggibilità.';
const TONE_CLAUSE = 'Tono sobrio, rispettoso ed elegante.';

const hasActiveGeometricFrame = (state) => {
  if (!isGeometricFrameStyle(state.frame.style)) {
    return false;
  }
  if (state.frame.style !== 'custom') {
    return true;
  }
  return Boolean((state.frame.customStyle || '').trim());
};

const hasContainedGeometricFrame = (state) =>
  hasActiveGeometricFrame(state) && state.frame.relation === 'contain';

// Profilo trasversale e intenzionalmente opzionale. Non riscrive le scelte
// dell’utente (palette, intensità della materia, ricchezza della cornice): ne
// governa la gerarchia visiva, così anche una resa ricca può restare leggibile
// al centro. Il confronto stretto con `true` preserva gli stati legacy.
const textReadabilityPrompt = (state) => {
  if (state.textReadability !== true) {
    return '';
  }
  const containment = hasContainedGeometricFrame(state)
    ? ' Sui tratti coperti, anche queste variazioni si arrestano al bordo interno della cornice e non passano sotto di essa.'
    : '';
  return `Priorità alla leggibilità dei testi: mantieni l’immagine complessivamente molto chiara, ariosa, desaturata e a contrasto basso. Lascia l’area centrale ancora più chiara, quasi uniforme e pulita; concentra eventuali variazioni cromatiche, texture, venature, riflessi e qualunque dettaglio richiesto verso le zone periferiche del fondo, dove restano riconoscibili ma secondari e non competono con foto e scritte.${containment}`;
};

// Asse dello sfumato simmetrico: dove torna il colore ai due estremi.
const MIRROR_AXIS = {
  top: 'in alto e in basso',
  bottom: 'in alto e in basso',
  left: 'a sinistra e a destra',
  right: 'a sinistra e a destra',
  'top-left': 'agli angoli opposti',
  'top-right': 'agli angoli opposti',
  'bottom-left': 'agli angoli opposti',
  'bottom-right': 'agli angoli opposti',
};

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : '';

// Testo libero di una dimensione «Personalizzata»: trim-mato, mai
// undefined/null. Il troncamento a CUSTOM_TEXT_MAX_LENGTH vive nel setter
// (builderState.js); qui serve solo capire se c’è davvero qualcosa da dire.
const customTextOf = (rawText) => (rawText || '').trim();

// Le nove dimensioni che ammettono 'custom' condividono la stessa forma: col
// testo pieno usa quello (nudo, o con l’involucro `wrap` per tecnica,
// atmosfera e palette); a selezione custom con testo vuoto ricade sulla voce
// di default del catalogo — mai una frase monca, mai un crash. Le chiavi non
// custom e sconosciute restano `undefined`, come già oggi (nessuna
// regressione sui rami esistenti: es. figura/materiale/intensità sconosciuti
// che già degradano senza rompersi).
const customCatalogPromptOf = (catalog, key, customText, defaultKey, wrap) => {
  if (key === 'custom') {
    const text = customTextOf(customText);
    if (text) {
      return wrap ? wrap(text) : text;
    }
    return findByKey(catalog, defaultKey).prompt;
  }
  const entry = findByKey(catalog, key);
  return entry ? entry.prompt : undefined;
};

const palettePromptOf = (state) =>
  customCatalogPromptOf(
    BUILDER_PALETTES,
    state.style.palette,
    state.style.customPalette,
    'none',
    (text) => `palette di colori: ${text}`
  ) || '';

// Resolver sottile: la regola «la carta è attiva?» vive in builderState.js
// (isPaperActive), non qui, perché anche l’anteprima del blueprint
// (blueprintPreview.js, che la usa a sua volta) deve leggere esattamente la
// stessa condizione — non una copia locale che può divergere da quella.
const activePaper = (state) =>
  isPaperActive(state) ? findByKey(BUILDER_PAPERS, state.base.paper) : null;

// gpt-image-2, sentendo «carta ruvida», tende a leggere «fotografa un
// foglio»: bordi, angoli, ombra portata, a volte un secondo foglio sotto. In
// uno sfondo da comporre entro margini e cornice è irrecuperabile. Lo neghiamo
// per esteso, come già
// facciamo con le croci spontanee in noExtrasClause e con i filetti fantasma
// delle cornici morbide. Il testo assegna esplicitamente la carta alla sola
// area del fondo, così non compete con margini o cornici geometriche.
const paperPrompt = (state) => {
  const paper = activePaper(state);
  if (!paper) {
    // Carta sconosciuta: non c’è supporto da descrivere, niente clausola.
    return '';
  }
  // Grana sconosciuta invece non azzera la clausola: la carta l’ha scelta
  // l’utente apposta, manca solo l’intensità della texture, e scartare
  // l’intera frase butterebbe via una decisione reale. Ricade sulla delicata.
  const grain = findPaperGrain(state.base.grain);
  return `L’intera area assegnata al fondo è resa come stampata su ${paper.prompt}, ${grain.prompt}. La texture è uniforme in questa area; il supporto non è la fotografia di un foglio appoggiato: nessun bordo o angolo di carta, nessuna ombra portata, nessuna piega o sovrapposizione visibile.`;
};

// Il modello tende a trasformare i nomi dei materiali in fotografie di
// lastre, pavimenti, panneggi o oggetti preziosi. Serve invece una texture
// frontale continua nella sola area del fondo: la guardia comune vieta la
// geometria dell’oggetto senza vietare margini o cornici richieste.
const materialPrompt = (state) => {
  if (state.base.type !== 'material') {
    return '';
  }
  const material = findByKey(BUILDER_MATERIALS, state.base.material);
  if (!material) {
    return '';
  }
  const intensity = findMaterialIntensity(state.base.materialIntensity);
  return `L’area assegnata al fondo ha una finitura materica continua in ${material.prompt}, ${intensity.prompt}. La finitura riempie in modo continuo questa area, in vista perfettamente frontale, e non è la fotografia di una lastra, una lapide, un oggetto o un ambiente: il materiale del fondo non forma bordi, spigoli, giunti, fughe, piani d’appoggio o prospettive; nessuna piega o panneggio; nessuna conchiglia, gioiello o frammento; nessun riflesso speculare né ombra portata.`;
};

// La figura sacra ha già oggi un fallback proprio (findSacredFigure ricade
// sulla Madonna per qualunque chiave non trovata, 'custom' compresa): con
// testo pieno lo si scavalca, a vuoto ci si appoggia esattamente a quello,
// così una chiave sconosciuta continua a non rompersi (vedi builderCatalog.js).
const sacredFigurePromptOf = (state) => {
  if (state.base.sacredFigure === 'custom') {
    const text = customTextOf(state.base.customSacredFigure);
    if (text) {
      return text;
    }
  }
  return findSacredFigure(state.base.sacredFigure).prompt;
};

const sacredScenePrompt = (state) => {
  return [
    sacredFigurePromptOf(state),
    customCatalogPromptOf(
      SACRED_FRAMINGS,
      state.base.sacredFraming,
      state.base.customSacredFraming,
      'halfbust'
    ),
    customCatalogPromptOf(
      SACRED_PERSPECTIVES,
      state.base.sacredPerspective,
      state.base.customSacredPerspective,
      'frontal'
    ),
    customCatalogPromptOf(
      SACRED_MOODS,
      state.base.sacredMood,
      state.base.customSacredMood,
      'serene'
    ),
  ]
    .filter(Boolean)
    .join(', ');
};

const scenePromptOf = (state, scene) => {
  if (state.base.sceneKey === 'custom') {
    const text = customTextOf(state.base.customScene);
    return text || findByKey(BUILDER_SCENES, 'sky').prompt;
  }
  return state.base.sceneKey === SACRED_SCENE_KEY
    ? sacredScenePrompt(state)
    : scene.prompt;
};

const gradientDirectionPrompt = (
  state,
  direction,
  { usesTone, startsFromWhite }
) => {
  if (direction.key !== 'edges' || !hasContainedGeometricFrame(state)) {
    return startsFromWhite
      ? direction.promptFromWhite
      : usesTone && direction.promptToTone
      ? direction.promptToTone
      : direction.prompt;
  }
  const boundary =
    'il perimetro utile del fondo: il bordo interno della cornice sui tratti coperti e il limite esterno del disegno sugli eventuali tratti aperti';
  if (startsFromWhite) {
    return `con il bianco disposto lungo ${boundary}, poi sfumato gradualmente verso il colore al centro`;
  }
  return usesTone
    ? `con il colore disposto lungo ${boundary}, poi sfumato gradualmente verso il centro`
    : `più intenso lungo ${boundary}, poi sfumato verso un centro quasi bianco`;
};

// Un colore libero non ha il toneName curato dei preset: lo si racconta al
// modello per famiglia di tinta e qualificatori, l'hex accanto fa da àncora.
const hexToHsl = (hex) => {
  const [r, g, b] = [1, 3, 5].map(
    (offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255
  );
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) {
      h = ((g - b) / d) % 6;
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h = (h * 60 + 360) % 360;
  }
  return { h, s, l };
};

const HUE_FAMILIES = [
  [15, 'rosso'],
  [40, 'arancio'],
  [65, 'giallo oro'],
  [150, 'verde'],
  [195, 'verde acqua'],
  [250, 'azzurro'],
  [290, 'viola'],
  [330, 'rosa violaceo'],
  [345, 'rosa acceso'],
  [360, 'rosso'],
];

export function describeHexTone(hex) {
  const { h, s, l } = hexToHsl(hex.toLowerCase());
  if (l >= 0.97) {
    return 'bianco';
  }
  if (l <= 0.08) {
    return 'quasi nero';
  }
  if (s < 0.1) {
    if (l > 0.75) {
      return 'grigio chiaro';
    }
    return l < 0.35 ? 'grigio scuro' : 'grigio neutro';
  }
  let family = HUE_FAMILIES.find(([limit]) => h < limit)[1];
  // I rossi ad alta luminosità sono percettivamente rosa (es. rosa antico).
  if ((h >= 330 || h < 15) && l > 0.6) {
    family = 'rosa';
  }
  const soft = s < 0.35 ? ' polveroso' : '';
  const light = l > 0.72 ? ' chiaro' : l < 0.36 ? ' profondo' : '';
  return `${family}${soft}${light}`;
}

// Nome «da prompt» di un estremo risolto: toneName per i preset, descrizione
// derivata più hex per i colori liberi. Entrambi invariabili nelle frasi.
const toneNameOf = (info) =>
  info.toneName || `${describeHexTone(info.hex)} (${info.hex})`;

const basePrompt = (state) => {
  const palettePrompt = palettePromptOf(state);
  if (state.base.type === 'gradient') {
    const direction = findByKey(GRADIENT_DIRECTIONS, state.base.direction);
    const spread = findByKey(GRADIENT_SPREADS, state.base.spread);
    const gradientIntensity =
      findByKey(GRADIENT_INTENSITIES, state.base.gradientIntensity) ||
      findByKey(GRADIENT_INTENSITIES, 'medium');
    const { from, to } = resolvedGradientEndpoints(state);
    const usesTone = !to.isWhite;
    const startsFromWhite = usesTone && from.isWhite;
    const spreadPrompt = spread
      ? startsFromWhite && spread.promptFromWhite !== undefined
        ? spread.promptFromWhite
        : spread.prompt
      : '';
    if (state.base.mirror && direction.key !== 'edges') {
      const centerPhrase = usesTone
        ? `verso i toni ${toneNameOf(to)} al centro`
        : 'verso un centro chiaro quasi bianco';
      const axis = MIRROR_AXIS[direction.key] || 'ai lati opposti';
      const edgePhrase = startsFromWhite
        ? `con il bianco ${axis} che sfuma ${centerPhrase}`
        : `con il colore ${
            !from.isWhite ? `dai toni ${toneNameOf(from)} ` : ''
          }${axis} che sfuma ${centerPhrase}`;
      const pieces = [
        'delicato sfondo sfumato simmetrico',
        edgePhrase,
        spreadPrompt,
        gradientIntensity.prompt,
      ];
      return `Base: ${pieces.filter(Boolean).join(', ')}.`;
    }
    let colorSegment;
    if (!usesTone) {
      // Arrivo bianco (di default o scelto): se la partenza è un colore
      // esplicito del pallino — non l'eredità dalla palette, che qui resta
      // sul fraseggio di sempre — il prompt deve nominarlo, altrimenti un
      // pallino colorato (es. rosa antico) sparisce dal prompt pur essendo
      // visibile nel foglio schematico e nel ramo mirror.
      colorSegment =
        state.base.fromColor && !from.isWhite
          ? `sfumatura dai toni ${toneNameOf(from)} verso il bianco`
          : palettePrompt;
    } else if (startsFromWhite) {
      colorSegment = `sfumatura dal bianco verso i toni ${toneNameOf(to)}`;
    } else {
      colorSegment = `sfumatura dai toni ${toneNameOf(
        from
      )} verso i toni ${toneNameOf(to)}`;
    }
    const directionSegment = gradientDirectionPrompt(state, direction, {
      usesTone,
      startsFromWhite,
    });
    const pieces = [
      'delicato sfondo sfumato',
      colorSegment,
      directionSegment,
      spreadPrompt,
      gradientIntensity.prompt,
    ];
    return `Base: ${pieces.filter(Boolean).join(', ')}.`;
  }
  if (state.base.type === 'scene') {
    const scene = findByKey(BUILDER_SCENES, state.base.sceneKey);
    const placement = findByKey(SCENE_PLACEMENTS, state.base.scenePlacement);
    if (!placement || placement.key === 'full') {
      return `Base: ${[scenePromptOf(state, scene), palettePrompt]
        .filter(Boolean)
        .join(', ')}.`;
    }
    const extent = findByKey(SCENE_EXTENTS, state.base.sceneExtent);
    const fade = findSceneFade(state.base.sceneFade !== false);
    const axisWord = placement.axis === 'height' ? 'altezza' : 'larghezza';
    const ground = palettePrompt
      ? `fondo a tinta leggera, ${palettePrompt}`
      : 'fondo bianco pulito e uniforme';
    return `Base: ${ground}; ${scenePromptOf(state, scene)}, ${
      placement.phrase
    }, che occupa ${extent.phrase} dell${
      axisWord === 'altezza' ? '’' : 'a '
    }${axisWord} ${fade.prompt}.`;
  }
  if (state.base.type === 'paper') {
    // La carta la nomina paperPrompt: qui resta solo la superficie, altrimenti
    // il supporto comparirebbe due volte nello stesso prompt.
    return palettePrompt
      ? `Base: superficie uniforme e pulita, ${palettePrompt}, senza paesaggi né elementi di scena.`
      : 'Base: superficie uniforme e pulita, bianca e neutra, senza dominanti crema o beige, senza paesaggi né elementi di scena.';
  }
  if (state.base.type === 'material') {
    // Il tipo preciso compare in materialPrompt, così resta nominato una sola
    // volta. La palette è una velatura cromatica, non cambia la materia scelta.
    return palettePrompt
      ? `Base: superficie materica continua e chiara, armonizzata con ${palettePrompt}, senza paesaggi né elementi di scena.`
      : 'Base: superficie materica continua e chiara nei toni naturali del materiale, senza paesaggi né elementi di scena.';
  }
  if (palettePrompt) {
    return `Base: sfondo a tinta unita pulito e uniforme, ${palettePrompt}.`;
  }
  return 'Base: sfondo bianco pulito e uniforme, senza paesaggi né elementi di scena.';
};

const techniquePromptOf = (state) =>
  customCatalogPromptOf(
    BUILDER_TECHNIQUES,
    state.style.technique,
    state.style.customTechnique,
    'minimal',
    (text) => `resa artistica: ${text}`
  );

const intensityPromptOf = (state) =>
  customCatalogPromptOf(
    BUILDER_INTENSITIES,
    state.style.intensity,
    state.style.customIntensity,
    'natural'
  );

const atmospherePromptOf = (state) =>
  customCatalogPromptOf(
    ATMOSPHERES,
    state.style.atmosphere,
    state.style.customAtmosphere,
    'neutral',
    (text) => `atmosfera e luce: ${text}`
  );

const stylePrompt = (state) => {
  if (!supportsAtmosphere(state)) {
    return '';
  }
  const pieces = [];
  if (hasImagery(state)) {
    pieces.push(techniquePromptOf(state), intensityPromptOf(state));
  }
  const atmosphereText = atmospherePromptOf(state);
  if (atmosphereText) {
    pieces.push(atmosphereText);
  }
  const description = pieces.filter(Boolean).join(', ');
  return description ? `${capitalize(description)}.` : '';
};

const elementDescription = (element) => {
  if (element.itemKey === CUSTOM_ELEMENT_KEY) {
    return element.customText;
  }
  const base = findElementItem(element.itemKey).prompt;
  const levers = elementFigureLevers(element.itemKey);
  if (!levers || !element.options) {
    return base;
  }
  const leverPrompts = levers
    .map((lever) => {
      const value = findByKey(lever.options, element.options[lever.key]);
      return value ? value.prompt : '';
    })
    .filter(Boolean);
  return [base, ...leverPrompts].join(', ');
};

const elementsPrompt = (state) => {
  if (!state.elements.length) {
    return '';
  }
  const parts = state.elements
    .map((element) => {
      const combo = findZoneCombo(element.cells);
      return `${elementDescription(element)} ${combo.phrase}`;
    })
    .join('; ');
  const readability =
    state.textReadability === true
      ? ' Gli elementi restano visibili ma non dominanti, a contrasto controllato e con i bordi morbidamente integrati nel fondo.'
      : '';
  return `Elementi decorativi, discreti e ben integrati nella scena: ${parts}.${readability}`;
};

// Stile e perimetro possono essere presi dal catalogo o, se l’utente ha
// scelto la voce "custom", dal testo libero digitato in stato.
const framePrompt = (state) => {
  const style = findByKey(FRAME_STYLES, state.frame.style);
  const styleText =
    style && style.key === 'custom'
      ? state.frame.customStyle
      : style && style.prompt;
  if (!styleText) {
    return '';
  }
  const coverage = findByKey(FRAME_COVERAGES, state.frame.coverage);
  const coverageText =
    coverage && coverage.key === 'custom'
      ? state.frame.customCoverage
      : coverage && coverage.prompt;
  const richness = findByKey(FRAME_RICHNESS, state.frame.richness);
  const body = [
    capitalize(styleText),
    richness && richness.prompt,
    coverageText,
  ]
    .filter(Boolean)
    .join(', ');

  // Stili morbidi (velatura, sfumatura, dissolvenza, ombra): NON sono cornici.
  // Diciamo «cornice» solo quando l’utente ne sceglie davvero una. Su questi
  // stili la parola «cornice», «spessore uniforme/simmetrico», la relazione
  // sovrapposta/contenitiva e la negazione che nomina «filetti/riquadri»
  // spingono gpt-image-2 a disegnare un filetto rettangolare inesistente.
  // Qui descriviamo solo, in positivo, un effetto pittorico e sfumato.
  if (style && style.soft) {
    const readability =
      state.textReadability === true
        ? ' L’intervento sui bordi resta leggero, poco contrastato e secondario rispetto all’area destinata ai testi.'
        : '';
    return `Trattamento morbido dei bordi: ${body}. L’effetto resta pittorico, morbido e sfumato, e si fonde gradualmente nel foglio; è l’unico intervento sui bordi e lascia ampio e libero lo spazio interno per la foto e i testi.${readability}`;
  }

  // Cornici vere e proprie: qui «sottile e di spessore uniforme» è voluto. Che
  // la cornice arrivi o meno ai bordi lo governa l’asse Margini (marginPrompt),
  // non la relazione: la relazione dice solo se il contenuto sta dentro la
  // cornice o le passa sotto.
  const relation = findByKey(FRAME_RELATIONS, state.frame.relation);
  const readability =
    state.textReadability === true
      ? ' La cornice resta leggera, poco contrastata e secondaria rispetto all’area destinata a foto e scritte.'
      : '';
  return `${body}; ${relation.prompt}. La cornice è mantenuta sottile e di spessore uniforme e simmetrico su tutti i tratti coperti, e lascia libero il più possibile lo spazio interno per la foto e i testi.${readability} Rendi unicamente la cornice descritta, senza aggiungere una seconda cornice o ulteriori contorni decorativi non richiesti.`;
};

const MARGIN_PHRASES = {
  none: 'a filo del bordo fisico del manifesto',
  minimal:
    'rientrato di pochi millimetri, lasciando all’esterno un margine bianco minimo',
  small:
    'rientrato di qualche millimetro, lasciando all’esterno un piccolo margine bianco',
};
const MARGIN_UNIFORM = {
  minimal: 'minimo, di pochi millimetri',
  small: 'di qualche millimetro',
};

// Il margine descrive esclusivamente lo spazio ESTERNO all'intero disegno. Se
// c'è una cornice geometrica, il suo bordo esterno fa parte di quel limite: la
// relazione overlay/contain governa invece soltanto ciò che accade al fondo
// sul bordo interno della cornice.
const marginPrompt = (state) => {
  const m = state.frame.margins || {};
  const sides = [m.top, m.bottom, m.left, m.right];
  const includesFrame = hasActiveGeometricFrame(state);
  const subject = includesFrame
    ? 'dell’intero disegno (cornice inclusa)'
    : 'del disegno';
  if (sides.every((s) => !s || s === 'none')) {
    return `Il limite esterno ${subject} coincide con i quattro bordi fisici del manifesto: non rimane alcun margine bianco esterno.`;
  }
  const allEqual = sides.every((s) => s === sides[0]);
  if (allEqual) {
    return `L’intero disegno${
      includesFrame ? ', cornice inclusa,' : ''
    } lascia all’esterno un margine bianco ${
      MARGIN_UNIFORM[sides[0]]
    } e uniforme su tutti i lati.`;
  }
  const p = (v) => MARGIN_PHRASES[v] || MARGIN_PHRASES.none;
  return `Posizione del limite esterno ${subject}: in alto ${p(
    m.top
  )}, in basso ${p(m.bottom)}, a sinistra ${p(m.left)}, a destra ${p(
    m.right
  )}.`;
};

// gpt-image-2, sentendo «manifesto funebre», tende ad aggiungere di sua
// iniziativa croci, fiori e immagini sacre anche quando non richiesti: lo
// vietiamo esplicitamente, preservando però ciò che l’utente ha chiesto.
const noExtrasClause = (state, hasDetails) => {
  return hasImagery(state) || hasDetails
    ? 'Non aggiungere spontaneamente simboli, croci, fiori, figure, immagini sacre, scritte o decorazioni oltre a quanto esplicitamente descritto sopra.'
    : 'Nessun simbolo, croce, fiore, figura, immagine sacra o scritta aggiunta: a parte quanto descritto, l’intera superficie resta pulita e vuota.';
};

const withFinalPeriod = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

// Il prompt non nomina il formato. gpt-image-2 riceve le misure in
// `image_size` e compone per quel telaio: misurato, lo stesso prompt reso a
// 2416x3424 e a 3424x2416 produce due composizioni corrette, e un prompt che
// dichiara «verticale» reso a misura orizzontale esce comunque orizzontale. La
// parola era inerte, e toglierla dimezza i prompt che un set deve esportare.
export function composeBuilderPrompt(state) {
  const details = (state.details || '').trim();
  const parts = [
    'Sfondo per manifesto funebre commemorativo, pensato per l’inserimento di testi ben leggibili sopra l’immagine.',
    basePrompt(state),
    stylePrompt(state),
    paperPrompt(state),
    materialPrompt(state),
    textReadabilityPrompt(state),
    elementsPrompt(state),
    marginPrompt(state),
    framePrompt(state),
    details ? withFinalPeriod(details) : '',
    noExtrasClause(state, Boolean(details)),
    CENTER_CLAUSE,
    TONE_CLAUSE,
  ];
  return parts.filter(Boolean).join(' ');
}
